import { getApiUrl, API_URL } from '@/utils/api';
import { AddressModel, CartModel } from '@/models';

import { LocalStorageService } from './LocalStorage.service';
import ApiBase from './ApiBase';
import { KEYS } from '@/constants/keys';
import { ADVANCED_SEARCH_RANGE } from '@/constants/admin';
import { COUNTRIES } from '@/constants/profile';

export default class CartService extends ApiBase {
  static userUrl = getApiUrl(API_URL.USER, '/cart');

  static getUserBoxUrl = (storeId: string) => `${CartService.userUrl}/${storeId}/box`;

  get(storeId: string, stepHT: boolean = false, storeIdForPriceCookie = null) {
    let options = {
      credentials: 'include'
    } as any;
    if (storeIdForPriceCookie) {
      options = { ...options, cookies: `storeIdForPrice=${storeIdForPriceCookie}` };
    }

    return this.api.getJSON(`${CartService.userUrl}/${storeId}?stepHT=${stepHT}`, options);
  }

  update(cart: CartModel) {
    return this.api.putJSON(`${CartService.userUrl}/${cart.storeId}`, cart);
  }

  updateAttribute(storeId: string, attributeName: string, newValue: any) {
    return this.api.postJSON(`${CartService.userUrl}/${storeId}/setCurrentCartAttribute`, { attributeName, newValue });
  }

  updateAttributeList(storeId: string, attributeList) {
    return this.api.postJSON(`${CartService.userUrl}/${storeId}/setCurrentCartAttributeList`, attributeList);
  }

  updatePartial(storeId: string, partialCart) {
    let action: string;

    if (partialCart.hasOwnProperty('discountCode')) {
      action = 'updateDiscountCode';
    } else if (partialCart.hasOwnProperty('selectedGift')) {
      action = 'updateGift';
    } else if (partialCart.hasOwnProperty('selectedGiftPack')) {
      action = 'updateGiftPack';
    }

    return this.api.putJSON(`${CartService.userUrl}/${storeId}/${action}`, partialCart);
  }

  pay({ cart, fingerprint = undefined }) {
    return this.api.putJSON(`${CartService.userUrl}/${cart.storeId}/processOrder`, fingerprint ?
      { ...cart, paiementMode: { ...cart.paiementMode, fingerprint: fingerprint } } :
      cart);
  }

  validate(cart, orderId?: string) {
    return this.api.getJSON(`${CartService.userUrl}/${cart.storeId}/validateOrder?orderId=${orderId}`);
  }

  validateV2(cart) {
    return this.api.putJSON(`${CartService.userUrl}/${cart.storeId}/V2/validateOrder`, cart);
  }

  validateOrderAdyen(storeId: string, orderId: string, md, paRes, adyenDetails) {
    return this.api.putJSON(`${CartService.userUrl}/${storeId}/validateOrderAdyen`, { orderId, md, paRes, adyenDetails });
  }

  validateOrderAdyenWithCart({ cart, state = undefined, redirectResult = undefined }) {
    return this.api.putJSON(`${CartService.userUrl}/${cart.storeId}/validateOrderAdyen`, { orderId: cart.id, details: { ...state?.data?.details, redirectResult } });
  }

  addProduct(storeId: string, partnumber: string, qty: number, custoText?: string) {
    return this.api.postJSON(`${CartService.userUrl}/${storeId}/product`, {
      partnumber,
      qty,
      custoText
    }, {
      guestToken: true,
      credentials: 'include'
    });
  }

  editWinebox(storeId: string, boxId: string) {
    return this.api.putJSON(CartService.getUserBoxUrl(storeId) + `?boxId=${boxId}`, null, { noJsonHeader: true });
  }

  addBox(storeId: string, boxId: string, qty: number) {
    return this.api.postJSON(CartService.getUserBoxUrl(storeId), {
      boxId,
      qty
    });
  }

  delete(storeId: string) {
    return this.api.deleteJSON(`${CartService.userUrl}/${storeId}`);
  }

  getShippingModes() {
    // TODO TYPESCRIPT: post without payload ???
    return this.api.postJSON(`${CartService.userUrl}/shipping`, undefined);
  }

  getPaymentModes(storeId: string, isApplePayEnabled = false) {
    return this.api.getJSON(`${CartService.userUrl}/${storeId}/getPayments?isApplePayEnabled=${isApplePayEnabled}`, {
      credentials: 'include'
    });
  }

  selectAddress(storeId: string, addressId: string, billing: boolean) {
    const prefix = billing ? 'Billing' : 'Shipping';
    return this.api.postJSON(`${CartService.userUrl}/${storeId}/set${prefix}Address`, { id: addressId });
  }

  getStripeSession(cart) {
    return this.api.putJSON(`${CartService.userUrl}/${cart.storeId}/getStripeSession`, cart);
  }

  getPaymentFromProvider(storeId: string, paymentMode, onlySaveCard = false) {
    return this.api.putJSON(`${CartService.userUrl}/${storeId}/getPaymentFromProvider`, { ...paymentMode, onlySaveCard });
  }

  addOrderToCart(storeId: string, orderId: string) {
    return this.api.putJSON(
      `${CartService.userUrl}/${storeId}/addOrderToCart`,
      { id: orderId },
      { credentials: 'include' }
    );
  }

  addOrderToCartSuccess(productsNotAdded: any[]) {
    if (productsNotAdded.length > 0) {
      const adminToasts = LocalStorageService.getObject(KEYS.ADMIN_TOASTS);

      const toastsToAdd = productsNotAdded.map((product) => {
        let urls;

        if (product?.attributes && product?.item) {
          const {
            attributes: {
              producteur: {
                value: producteur
              },
              appellation: {
                valueId: appelation
              },
              millesime: {
                valueId: millesime
              }
            },
            item: { offerPrice }
          } = product;

          const price = offerPrice >= ADVANCED_SEARCH_RANGE ? (offerPrice - ADVANCED_SEARCH_RANGE) : 0;
          urls = {
            simpleFilter: `/search?searchTerm=${producteur}`,
            advancedFilter: `/tous-nos-vins.html?filter=appellation:${appelation}|millesime:${millesime}` +
              `|minPrice:${price}|maxPrice:${offerPrice + ADVANCED_SEARCH_RANGE}`
          };
        }

        return {
          message: `{{product.noStock}} : ${product.partnumber}`,
          urls
        };
      });

      if (adminToasts && Array.isArray(adminToasts)) {
        LocalStorageService.setObject(KEYS.ADMIN_TOASTS, adminToasts.concat(toastsToAdd));
      } else {
        LocalStorageService.setObject(KEYS.ADMIN_TOASTS, toastsToAdd);
      }
    }
  }

  deleteStoreIdForCart(storeId: string) {
    return this.api.deleteJSON(`${CartService.userUrl}/${storeId}/deleteStoreIdForCart`, {});
  }

  getPaymentMethodsForApplePay(storeId: string, paymentMode) {
    return this.api.putJSON(`${CartService.userUrl}/${storeId}/getPaymentMethodsForApplePay`, paymentMode);
  }

  makeApplePayPayment(
    storeId: string,
    applePayToken,
    paymentMode,
    shippingMode,
    shippingAddress,
    billingAddress
  ) {
    return this.api.putJSON(
      `${CartService.userUrl}/${storeId}/applePayPayment`,
      { applePayToken, paymentMode, shippingMode, shippingAddress, billingAddress }
    );
  }
}
